import {
  SEARCH_RESULT_CLEAR,
  SEARCH_RESULT_SAVE_PROFILE,
  SEARCH_RESULT_SCHOOL_START,
  SEARCH_RESULT_SCHOOL_UPDATE,
  SEARCH_RESULT_SET_EXPANDED_SCHOOL,
  SEARCH_RESULT_SET_PARAMETERS,
  SEARCH_RESULT_START,
  SEARCH_RESULT_UPDATE,
  SEARCH_SET_META,
  SEARCH_SET_SHOULD_VALIDATE_ELIGIBILITY,
  SEARCH_SET_TAB,
  SEARCH_NEXT_PAGE,
  SEARCH_SET_PAGE_LOADING,
  ActionType,
} from '../actions/search';

export type ProgramInfo = {
  commission: string;
  commission_rate: string;
  fee: string;
  id: number;
  len: string;
  level: string;
  name: string;
  ptime: number;
  slug: string;
  tuition: string;
};

export type ProgramListInfo = {
  school_id: number;
  count: number;
  programs: Array<ProgramInfo>;
};

type ISearchResult = null | Array<ProgramListInfo>;

export type ISearchStoreState = {
  currentPage: number;
  hasStarted: boolean;
  searchResults: ISearchResult;
  isLoading: boolean;
  isPageLoading: boolean;
  expandedSchool: void | number | string;
  schoolResults: {
    [key: string]: {
      isLoading: boolean;
      searchResults: ISearchResult;
    };
  };
  profiles: {
    [key: string]: {
      student_id: number;
    };
  };
  meta: {
    page?: number;
    pageSize?: number;
    totalPages?: number;
    totalPrograms?: number;
    totalSchools?: number;
  };
  searchParameters: any;
  shouldValidateEligibility: boolean;
  tab: 'programs' | 'schools';
};

function getInitialTab(): 'programs' | 'schools' {
  if (window && window.location && window.location.hash && window.location.hash.length > 0) {
    const locationHash = window.location.hash.substr(1);
    if (locationHash === 'programs' || locationHash === 'schools') {
      return locationHash;
    }
  }

  if (
    typeof window.location.pathname === 'string' &&
    window.location.pathname.startsWith('/schools')
  ) {
    return 'schools';
  }

  return 'programs';
}

const defaultState: ISearchStoreState = {
  currentPage: 1,
  expandedSchool: null,
  hasStarted: false,
  isLoading: false,
  isPageLoading: false,
  meta: {},
  profiles: {},
  schoolResults: {},
  searchParameters: null,
  searchResults: null,
  shouldValidateEligibility: false,
  tab: getInitialTab(),
};

const calculatePage = (state: ISearchStoreState, pageNumber: number) =>
  Math.min((state.meta && state.meta.totalPages) || 1, pageNumber);

const searchReducer = (state = defaultState, action: ActionType): ISearchStoreState => {
  if (!action) return state;

  // eslint-disable-next-line default-case
  switch (action.type) {
    case SEARCH_RESULT_START:
      return {
        ...state,
        hasStarted: true,
        searchResults: null,
        isLoading: true,
        expandedSchool: null,
        schoolResults: {},
      };

    case SEARCH_SET_PAGE_LOADING:
      return {
        ...state,
        isPageLoading: action.value,
      };

    case SEARCH_RESULT_UPDATE:
      return {
        ...state,
        searchResults: action.append ? [...state.searchResults, ...action.value] : action.value,
        isLoading: false,
        expandedSchool: null,
        schoolResults: {},
      };
    case SEARCH_RESULT_SCHOOL_START:
      return {
        ...state,
        schoolResults: {
          ...state.schoolResults,
          [action.programId]: {
            isLoading: true,
            searchResults: null,
          },
        },
      };
    case SEARCH_RESULT_SCHOOL_UPDATE:
      return {
        ...state,
        schoolResults: {
          ...state.schoolResults,
          [action.programId]: {
            isLoading: false,
            searchResults: action.value,
          },
        },
      };

    case SEARCH_RESULT_SET_EXPANDED_SCHOOL:
      return {
        ...state,
        expandedSchool: action.value,
      };

    case SEARCH_RESULT_SAVE_PROFILE:
      return {
        ...state,
        profiles: {
          ...state.profiles,
          [action.value.student_id]: action.value,
        },
      };

    case SEARCH_RESULT_CLEAR:
      return {
        ...state,
        searchResults: null,
        hasStarted: false,
      };

    case SEARCH_RESULT_SET_PARAMETERS:
      return {
        ...state,
        searchParameters: action.value,
        currentPage: 1,
      };

    case SEARCH_SET_TAB:
      return {
        ...state,
        tab: action.value,
      };

    case SEARCH_SET_META:
      return {
        ...state,
        meta: action.value,
      };

    case SEARCH_NEXT_PAGE:
      return {
        ...state,
        currentPage: calculatePage(state, state.currentPage + 1),
      };

    case SEARCH_SET_SHOULD_VALIDATE_ELIGIBILITY:
      return {
        ...state,
        shouldValidateEligibility: action.value,
      };
  }

  return state;
};

export default searchReducer;
